import {
  SignalCategorySchema,
  type SignalCategoryType,
} from './signal-category.registry';
import { z } from 'zod';

export type SecurityClassification = {
  cweIds: string[];
  owaspCategory: string;
};

export const OWASP_TOP_10_2025_LABELS: Record<string, string> = {
  'A01:2025': 'Broken Access Control',
  'A02:2025': 'Security Misconfiguration',
  'A03:2025': 'Software Supply Chain Failures',
  'A04:2025': 'Cryptographic Failures',
  'A05:2025': 'Injection',
  'A06:2025': 'Insecure Design',
  'A07:2025': 'Authentication Failures',
  'A08:2025': 'Software or Data Integrity Failures',
  'A09:2025': 'Security Logging and Alerting Failures',
  'A10:2025': 'Mishandling of Exceptional Conditions',
};

export const OWASP_TOP_10_2025_CATEGORIES = Object.keys(
  OWASP_TOP_10_2025_LABELS,
) as (keyof typeof OWASP_TOP_10_2025_LABELS)[];

export const OWASP_AGENTIC_TOP_10_LABELS: Record<string, string> = {
  ASI01: 'Agent Goal Hijack',
  ASI02: 'Tool Misuse and Exploitation',
  ASI03: 'Identity and Privilege Abuse',
  ASI04: 'Agentic Supply Chain Vulnerabilities',
  ASI05: 'Unexpected Code Execution',
  ASI06: 'Memory and Context Poisoning',
  ASI07: 'Insecure Inter-Agent Communication',
  ASI08: 'Cascading Failures',
  ASI09: 'Human-Agent Trust Exploitation',
  ASI10: 'Rogue Agents',
};

export const OWASP_AGENTIC_TOP_10_CATEGORIES = Object.keys(
  OWASP_AGENTIC_TOP_10_LABELS,
) as (keyof typeof OWASP_AGENTIC_TOP_10_LABELS)[];

export const OWASP_CLASSIFICATION_LABELS: Record<string, string> = {
  ...OWASP_TOP_10_2025_LABELS,
  ...OWASP_AGENTIC_TOP_10_LABELS,
};

export const OWASP_CLASSIFICATION_CATEGORIES = Object.keys(
  OWASP_CLASSIFICATION_LABELS,
);

export const SIGNAL_CATEGORY_CLASSIFICATION: Record<
  SignalCategoryType,
  SecurityClassification
> = {
  ACL_ABUSE: { cweIds: ['CWE-732'], owaspCategory: 'A01:2025' },
  ADCS_ABUSE: { cweIds: ['CWE-287'], owaspCategory: 'A07:2025' },
  AGENT_GOAL_HIJACK: { cweIds: ['CWE-74'], owaspCategory: 'ASI01' },
  AGENTIC_SUPPLY_CHAIN: { cweIds: ['CWE-494'], owaspCategory: 'ASI04' },
  ARBITRARY_FILE_READ: { cweIds: ['CWE-22'], owaspCategory: 'A01:2025' },
  ARBITRARY_FILE_WRITE: { cweIds: ['CWE-732'], owaspCategory: 'A01:2025' },
  AS_REP_ROASTING: { cweIds: ['CWE-287'], owaspCategory: 'A07:2025' },
  BFLA: { cweIds: ['CWE-285'], owaspCategory: 'A01:2025' },
  BOLA: { cweIds: ['CWE-639'], owaspCategory: 'A01:2025' },
  BOPLA: { cweIds: ['CWE-915'], owaspCategory: 'A01:2025' },
  BROKEN_ACCESS: { cweIds: ['CWE-284'], owaspCategory: 'A01:2025' },
  BROKEN_AUTHENTICATION: { cweIds: ['CWE-287'], owaspCategory: 'A07:2025' },
  CODE_INJECTION: { cweIds: ['CWE-94'], owaspCategory: 'A05:2025' },
  COMMAND_INJECTION: { cweIds: ['CWE-77'], owaspCategory: 'A05:2025' },
  CONSTRAINED_DELEGATION: { cweIds: ['CWE-287'], owaspCategory: 'A07:2025' },
  CORS: { cweIds: ['CWE-942'], owaspCategory: 'A01:2025' },
  CSV_INJECTION: { cweIds: ['CWE-1236'], owaspCategory: 'A05:2025' },
  CSRF: { cweIds: ['CWE-352'], owaspCategory: 'A01:2025' },
  CVE: { cweIds: ['CWE-1104'], owaspCategory: 'A03:2025' },
  CASCADING_FAILURES: { cweIds: ['CWE-754'], owaspCategory: 'ASI08' },
  DATA_EXPOSURE: { cweIds: ['CWE-200'], owaspCategory: 'A01:2025' },
  DEFAULT_CREDENTIALS: { cweIds: ['CWE-1188'], owaspCategory: 'A07:2025' },
  DCSYNC: { cweIds: ['CWE-287'], owaspCategory: 'A07:2025' },
  DOM_XSS: { cweIds: ['CWE-79'], owaspCategory: 'A05:2025' },
  EMAIL_SECURITY: { cweIds: ['CWE-16'], owaspCategory: 'A02:2025' },
  EXPOSED_ADMIN_PANEL: { cweIds: ['CWE-16'], owaspCategory: 'A02:2025' },
  EXPOSED_SERVICE: { cweIds: ['CWE-16'], owaspCategory: 'A02:2025' },
  FILE_INCLUSION: { cweIds: ['CWE-98'], owaspCategory: 'A05:2025' },
  FILE_UPLOAD: { cweIds: ['CWE-434'], owaspCategory: 'A08:2025' },
  GPO_ABUSE: { cweIds: ['CWE-732'], owaspCategory: 'A01:2025' },
  HTTP_REQUEST_SMUGGLING: { cweIds: ['CWE-444'], owaspCategory: 'A05:2025' },
  HTTP_RESPONSE_SPLITTING: { cweIds: ['CWE-113'], owaspCategory: 'A05:2025' },
  HUMAN_AGENT_TRUST_EXPLOITATION: {
    cweIds: ['CWE-20'],
    owaspCategory: 'ASI09',
  },
  IDOR: { cweIds: ['CWE-639'], owaspCategory: 'A01:2025' },
  IDENTITY_PRIVILEGE_ABUSE: { cweIds: ['CWE-269'], owaspCategory: 'ASI03' },
  INFORMATION_DISCLOSURE: { cweIds: ['CWE-209'], owaspCategory: 'A10:2025' },
  INSECURE_DESERIALIZATION: { cweIds: ['CWE-502'], owaspCategory: 'A08:2025' },
  INSECURE_INTER_AGENT_COMMUNICATION: {
    cweIds: ['CWE-306'],
    owaspCategory: 'ASI07',
  },
  INSECURE_RANDOMNESS: { cweIds: ['CWE-330'], owaspCategory: 'A04:2025' },
  INSECURE_TRANSPORT: { cweIds: ['CWE-319'], owaspCategory: 'A04:2025' },
  KERBEROASTING: { cweIds: ['CWE-287'], owaspCategory: 'A07:2025' },
  LDAP_INJECTION: { cweIds: ['CWE-90'], owaspCategory: 'A05:2025' },
  LOCAL_PRIVILEGE_ESCALATION: {
    cweIds: ['CWE-269'],
    owaspCategory: 'A01:2025',
  },
  MASS_ASSIGNMENT: { cweIds: ['CWE-915'], owaspCategory: 'A01:2025' },
  MEMORY_CONTEXT_POISONING: { cweIds: ['CWE-74'], owaspCategory: 'ASI06' },
  AI_CACHE_POISONING: { cweIds: ['CWE-524'], owaspCategory: 'ASI06' },
  NOSQL_INJECTION: { cweIds: ['CWE-943'], owaspCategory: 'A05:2025' },
  OPEN_REDIRECT: { cweIds: ['CWE-601'], owaspCategory: 'A01:2025' },
  OTHER: { cweIds: ['CWE-20'], owaspCategory: 'A10:2025' },
  PADDING_ORACLE: { cweIds: ['CWE-203'], owaspCategory: 'A04:2025' },
  PARAMETER_POLLUTION: { cweIds: ['CWE-235'], owaspCategory: 'A05:2025' },
  PATH_TRAVERSAL: { cweIds: ['CWE-22'], owaspCategory: 'A01:2025' },
  PROMPT_INJECTION: { cweIds: ['CWE-74'], owaspCategory: 'ASI01' },
  TOOL_POISONING: { cweIds: ['CWE-494'], owaspCategory: 'ASI04' },
  PROTOTYPE_POLLUTION: { cweIds: ['CWE-1321'], owaspCategory: 'A08:2025' },
  RACE_CONDITION: { cweIds: ['CWE-362'], owaspCategory: 'A10:2025' },
  REFLECTED_XSS: { cweIds: ['CWE-79'], owaspCategory: 'A05:2025' },
  ROGUE_AGENTS: { cweIds: ['CWE-284'], owaspCategory: 'ASI10' },
  RESOURCE_BASED_DELEGATION: { cweIds: ['CWE-287'], owaspCategory: 'A07:2025' },
  SERVER_MISCONFIGURATION: { cweIds: ['CWE-16'], owaspCategory: 'A02:2025' },
  SESSION_FIXATION: { cweIds: ['CWE-384'], owaspCategory: 'A07:2025' },
  SMB_RELAY: { cweIds: ['CWE-287'], owaspCategory: 'A07:2025' },
  SQL_INJECTION: { cweIds: ['CWE-89'], owaspCategory: 'A05:2025' },
  SSRF: { cweIds: ['CWE-918'], owaspCategory: 'A01:2025' },
  SSTI: { cweIds: ['CWE-1336'], owaspCategory: 'A05:2025' },
  STORED_XSS: { cweIds: ['CWE-79'], owaspCategory: 'A05:2025' },
  SUBDOMAIN_TAKEOVER: { cweIds: ['CWE-16'], owaspCategory: 'A02:2025' },
  TOOL_MISUSE: { cweIds: ['CWE-20'], owaspCategory: 'ASI02' },
  UNEXPECTED_CODE_EXECUTION: { cweIds: ['CWE-94'], owaspCategory: 'ASI05' },
  UNCONSTRAINED_DELEGATION: { cweIds: ['CWE-287'], owaspCategory: 'A07:2025' },
  WEAK_CRYPTO: { cweIds: ['CWE-327'], owaspCategory: 'A04:2025' },
  XPATH_INJECTION: { cweIds: ['CWE-91'], owaspCategory: 'A05:2025' },
  XXE: { cweIds: ['CWE-611'], owaspCategory: 'A05:2025' },
  VULNERABILITY_CHAINING: { cweIds: ['CWE-837'], owaspCategory: 'A10:2025' },
  EXCESSIVE_AGENCY: { cweIds: ['CWE-284'], owaspCategory: 'ASI02' },
};

export function getDefaultClassificationForCategory(
  category: SignalCategoryType,
): SecurityClassification {
  return (
    SIGNAL_CATEGORY_CLASSIFICATION[category] ??
    SIGNAL_CATEGORY_CLASSIFICATION.OTHER
  );
}

export function getOwaspCategoryLabel(owaspCategory: string): string {
  return OWASP_CLASSIFICATION_LABELS[owaspCategory] ?? owaspCategory;
}

export const CWE_LABELS: Record<string, string> = {
  'CWE-16': 'Configuration',
  'CWE-20': 'Improper Input Validation',
  'CWE-22': 'Path Traversal',
  'CWE-74': 'Injection',
  'CWE-77': 'Command Injection',
  'CWE-79': 'Cross-site Scripting (XSS)',
  'CWE-89': 'SQL Injection',
  'CWE-90': 'LDAP Injection',
  'CWE-91': 'XML Injection (XPath)',
  'CWE-94': 'Code Injection',
  'CWE-98': 'PHP Remote File Inclusion',
  'CWE-113': 'HTTP Response Splitting',
  'CWE-200': 'Exposure of Sensitive Information',
  'CWE-203': 'Observable Discrepancy',
  'CWE-209': 'Generation of Error Message Containing Sensitive Information',
  'CWE-235': 'Improper Handling of Extra Values',
  'CWE-250': 'Execution with Unnecessary Privileges',
  'CWE-269': 'Improper Privilege Management',
  'CWE-284': 'Improper Access Control',
  'CWE-285': 'Improper Authorization',
  'CWE-287': 'Improper Authentication',
  'CWE-290': 'Authentication Bypass by Spoofing',
  'CWE-306': 'Missing Authentication for Critical Function',
  'CWE-319': 'Cleartext Transmission of Sensitive Information',
  'CWE-326': 'Inadequate Encryption Strength',
  'CWE-327': 'Use of a Broken or Risky Cryptographic Algorithm',
  'CWE-330': 'Use of Insufficiently Random Values',
  'CWE-352': 'Cross-Site Request Forgery (CSRF)',
  'CWE-362': 'Race Condition',
  'CWE-384': 'Session Fixation',
  'CWE-400': 'Uncontrolled Resource Consumption',
  'CWE-770': 'Allocation of Resources Without Limits or Throttling',
  'CWE-434': 'Unrestricted Upload of File with Dangerous Type',
  'CWE-444': 'HTTP Request Smuggling',
  'CWE-494': 'Download of Code Without Integrity Check',
  'CWE-502': 'Deserialization of Untrusted Data',
  'CWE-522': 'Insufficiently Protected Credentials',
  'CWE-524': 'Use of Cache Containing Sensitive Information',
  'CWE-601': 'URL Redirection to Untrusted Site',
  'CWE-611': 'XML External Entity (XXE)',
  'CWE-639': 'Authorization Bypass Through User-Controlled Key',
  'CWE-696': 'Incorrect Behavior Order',
  'CWE-732': 'Incorrect Permission Assignment for Critical Resource',
  'CWE-754': 'Improper Check for Unusual or Exceptional Conditions',
  'CWE-778': 'Insufficient Logging',
  'CWE-837': 'Improper Enforcement of a Single, Unique Action',
  'CWE-915':
    'Improperly Controlled Modification of Dynamically-Determined Object Attributes',
  'CWE-918': 'Server-Side Request Forgery (SSRF)',
  'CWE-942': 'Permissive Cross-domain Policy with Untrusted Domains',
  'CWE-943': 'Improper Neutralization of Special Elements in Data Query Logic',
  'CWE-1104': 'Use of Unmaintained Third Party Components',
  'CWE-1188': 'Initialization of a Resource with an Insecure Default',
  'CWE-1236': 'Improper Neutralization of Formula Elements in a CSV File',
  'CWE-1321':
    'Improperly Controlled Modification of Object Prototype Attributes',
  'CWE-1336':
    'Improper Neutralization of Special Elements Used in a Template Engine',
  'CWE-1385': 'Missing Origin Validation in WebSockets',
};

export function getCweLabel(cweId: string): string {
  return CWE_LABELS[cweId] ?? cweId;
}

const owaspCategorySet = new Set<string>(OWASP_CLASSIFICATION_CATEGORIES);

export function normalizeCweId(value: string): string | null {
  const trimmed = value.trim();

  if (!trimmed) {
    return null;
  }

  const upper = trimmed.toUpperCase();
  const prefixedMatch = upper.match(/^CWE-(\d+)$/);

  if (prefixedMatch) {
    return `CWE-${prefixedMatch[1]}`;
  }

  const digitsOnlyMatch = trimmed.match(/^(\d+)$/);

  if (digitsOnlyMatch) {
    return `CWE-${digitsOnlyMatch[1]}`;
  }

  return null;
}

export function normalizeCweIds(
  cweIds: readonly string[] | null | undefined,
): string[] {
  if (!cweIds?.length) {
    return [];
  }

  const seen = new Set<string>();
  const normalized: string[] = [];

  for (const raw of cweIds) {
    const cweId = normalizeCweId(raw);

    if (!cweId || seen.has(cweId)) {
      continue;
    }

    seen.add(cweId);
    normalized.push(cweId);
  }

  return normalized;
}

export function resolveOwaspCategory({
  owaspCategory,
  defaultOwaspCategory,
}: {
  owaspCategory?: string | null;
  defaultOwaspCategory: string;
}): string {
  if (owaspCategory && owaspCategorySet.has(owaspCategory)) {
    return owaspCategory;
  }

  return defaultOwaspCategory;
}

export function resolveSecurityClassification({
  category,
  cweIds,
  owaspCategory,
}: {
  category: SignalCategoryType;
  cweIds?: string[] | null;
  owaspCategory?: string | null;
}): SecurityClassification {
  const defaults = getDefaultClassificationForCategory(category);
  const normalizedCweIds = normalizeCweIds(cweIds);

  return {
    cweIds: normalizedCweIds.length ? normalizedCweIds : defaults.cweIds,
    owaspCategory: resolveOwaspCategory({
      owaspCategory,
      defaultOwaspCategory: defaults.owaspCategory,
    }),
  };
}

const signalCategorySet = new Set<string>(SignalCategorySchema.options);

export function isSignalCategory(value: string): value is SignalCategoryType {
  return signalCategorySet.has(value);
}

export const CLASSIFICATION_CWE_FILTER_OPTIONS = [
  ...new Set(
    Object.values(SIGNAL_CATEGORY_CLASSIFICATION).flatMap(
      (classification) => classification.cweIds,
    ),
  ),
].sort();

export const CweIdSchema = z
  .string()
  .regex(/^CWE-\d+$/, 'Must be CWE-<number> format');

export function buildCweOwaspReferenceForPrompt(): string {
  return Object.entries(SIGNAL_CATEGORY_CLASSIFICATION)
    .map(
      ([category, { cweIds, owaspCategory }]) =>
        `${category}: ${cweIds.join(', ')} (${owaspCategory})`,
    )
    .join('\n');
}

export function buildOwaspCategoryLegendForPrompt(): string {
  const webLegend = OWASP_TOP_10_2025_CATEGORIES.map(
    (code) => `${code}: ${OWASP_TOP_10_2025_LABELS[code]}`,
  ).join('\n');
  const agenticLegend = OWASP_AGENTIC_TOP_10_CATEGORIES.map(
    (code) => `${code}: ${OWASP_AGENTIC_TOP_10_LABELS[code]}`,
  ).join('\n');

  return `OWASP Top 10:2025 (web):
${webLegend}

OWASP Agentic Top 10 (AI / agentic):
${agenticLegend}`;
}

export function buildCweOwaspReferenceBlockForPrompt(): string {
  return `<cwe_reference>
Default CWE and OWASP category per signal category (only override when a more specific CWE is warranted).
Web application categories use OWASP Top 10:2025 (A01:2025–A10:2025).
Agentic / AI application categories use OWASP Agentic Top 10 (ASI01–ASI10).

${buildOwaspCategoryLegendForPrompt()}

Category defaults:
${buildCweOwaspReferenceForPrompt()}
</cwe_reference>`;
}
